import api from './api'
import type { ApiResponse } from '@/types/api.types'

export interface DashboardStats {
  totalEmployees: number
  activeEmployees: number
  pendingRequests: number
  activeProjects: number
  upcomingPayroll: string
  /** Çalışan bazlı, henüz onaylanmamış doküman ataması sayısı */
  pendingDocumentAcknowledgements?: number
}

export interface DashboardActivity {
  id: string
  type: string
  title: string
  description: string
  timestamp: string
  actor: string
}

export interface UpcomingBirthday {
  id: string
  name: string
  department: string | null
  position: string | null
  avatarUrl: string | null
  birthDate: string
  nextBirthday: string
  daysUntil: number
  month: number
  day: number
}

export interface WorkAnniversary {
  id: string
  name: string
  department: string | null
  position: string | null
  startDate: string
  years: number
}

export interface ExpiringSecurityCard {
  id: string
  name: string
  position: string | null
  securityCardNumber: string | null
  securityCardType: string | null
  expiryDate: string
  daysUntil: number
}

export interface CalendarEvent {
  kind: 'LEAVE' | 'BIRTHDAY'
  id: string
  title: string
  type: string
  startDate: string
  endDate: string
}

export interface AnnualLeaveSummary {
  year: number
  totalEntitlementDays: number
  totalUsedDays: number
  remainingDays: number
  nextMonthPlannedDays: number
  nextMonthEntries: Array<{
    id: string
    name: string
    startDate: string
    endDate: string
  }>
}

export interface PayrollViewStats {
  period: string | null
  total: number
  viewed: number
  notViewed: number
  viewedPct: number
  notViewedList?: Array<{
    employeeId: string
    name: string
    department: string | null
  }>
}

export interface RequestTrendPoint {
  month: string
  leave: number
  advance: number
  total: number
}

export interface LeaveTypeBreakdown {
  type: string
  label: string
  count: number
}

export interface DashboardData {
  stats: DashboardStats
  recentActivity: DashboardActivity[]
  departmentDistribution: Array<{
    name: string
    count: number
  }>
  upcomingBirthdays?: UpcomingBirthday[]
  todayBirthdays?: UpcomingBirthday[]
  todayAnniversaries?: WorkAnniversary[]
  expiringSecurityCards?: ExpiringSecurityCard[]
  calendarEvents?: CalendarEvent[]
  annualLeaveSummary?: AnnualLeaveSummary
  payrollViewStats?: PayrollViewStats
  requestTrend?: RequestTrendPoint[]
  leaveTypeBreakdown?: LeaveTypeBreakdown[]
}

export interface PortalSupervisor {
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
  phone: string | null
  title: string | null
}

export interface PortalDashboardData {
  leaveBalance: number
  totalAllowedLeave: number
  usedAnnualLeaveDays: number
  pendingRequests: number
  pendingDocuments: number
  upcomingLeave: Array<{ id: string; type: string; startDate: string; endDate: string }>
  recentPayslips: Array<{ id: string; period: string; date: string; status: string }>
  supervisor: PortalSupervisor | null
  /** İşe giriş tarihi (kıdem hesabı için) */
  startDate?: string | null
  /** Sunucuda hesaplanmış kıdem */
  seniority?: { years: number; months: number; days: number } | null
}

export type CelebrationKind = 'BIRTHDAY' | 'ANNIVERSARY'

export interface PendingCelebration {
  kind: CelebrationKind
  /** Yalnızca iş yıl dönümünde: tamamlanan yıl */
  years?: number
}

export const dashboardService = {
  async getDashboardData(): Promise<ApiResponse<DashboardData>> {
    const response = await api.get('/dashboard')
    return response.data
  },

  async getPortalDashboard(): Promise<ApiResponse<PortalDashboardData>> {
    const response = await api.get('/dashboard/portal')
    return response.data
  },

  /** Giriş yapan kişiye bugün gösterilecek (henüz kapatılmamış) doğum günü / yıl dönümü görselleri */
  async getCelebration(): Promise<ApiResponse<{ date: string; items: PendingCelebration[] }>> {
    const response = await api.get('/dashboard/celebration')
    return response.data
  },

  async markCelebrationSeen(kind: CelebrationKind): Promise<void> {
    await api.post('/dashboard/celebration/seen', { kind })
  }
}
